import * as Sentry from '@sentry/browser'
import { Map as MapboxMap, type MapEventOf } from 'mapbox-gl'

import { REMOTE_TILESET_LAYER } from './mapboxConstants'

const RETRY_DELAYS_MS = [1000, 3000, 10_000, 30_000, 60_000] as const
const VERIFICATION_TIMEOUT_MS = 5000

type Timer = ReturnType<typeof setTimeout>

type RuntimeEvent =
  | {
      readonly _tag: 'SourceFailed'
      readonly sourceId: string
      readonly errorClass: string
    }
  | { readonly _tag: 'MapIdle' }
  | { readonly _tag: 'StyleReplaced' }
  | { readonly _tag: 'SourcesChanged' }

type ReloadResult =
  | { readonly _tag: 'Reloaded'; readonly host: string | undefined }
  | { readonly _tag: 'SourceMissing' }

type SourceStatus = 'loaded' | 'loading' | 'missing'

type ExhaustedRetry = {
  readonly sourceId: string
  readonly host: string | undefined
  readonly attempts: number
  readonly errorClass: string
}

type RetryState =
  | {
      readonly _tag: 'Scheduled'
      readonly attempt: number
      readonly errorClass: string
      readonly host: string | undefined
      readonly timer: Timer
    }
  | {
      readonly _tag: 'Verifying'
      readonly attempt: number
      readonly errorClass: string
      readonly host: string | undefined
      readonly timer: Timer
    }
  | { readonly _tag: 'Exhausted' }

/** Runtime effects required by remote tileset recovery. */
export interface RemoteTilesetRecoveryRuntime {
  subscribe(listener: (event: RuntimeEvent) => void): () => void
  reloadSource(sourceId: string): ReloadResult
  getSourceStatus(sourceId: string): SourceStatus
  reportExhausted(retry: ExhaustedRetry): void
}

/** Active remote tileset recovery lifecycle. */
export interface RemoteTilesetRecovery {
  dispose(): void
}

class Recovery implements RemoteTilesetRecovery {
  private readonly states = new Map<string, RetryState>()
  private readonly unsubscribe: () => void
  private isDisposed = false

  public constructor(private readonly runtime: RemoteTilesetRecoveryRuntime) {
    this.unsubscribe = runtime.subscribe(this.onRuntimeEvent)
  }

  public dispose(): void {
    if (this.isDisposed) {
      return
    }
    this.isDisposed = true
    this.unsubscribe()
    this.clearAll()
  }

  private readonly onRuntimeEvent = (event: RuntimeEvent): void => {
    if (this.isDisposed) {
      return
    }

    switch (event._tag) {
      case 'SourceFailed':
        this.onSourceFailed(event)
        return
      case 'MapIdle':
        this.resetLoadedSources()
        return
      case 'StyleReplaced':
        this.clearAll()
        return
      case 'SourcesChanged':
        this.clearRemovedSources()
    }
  }

  private onSourceFailed(
    event: Extract<RuntimeEvent, { _tag: 'SourceFailed' }>,
  ): void {
    const current = this.states.get(event.sourceId)
    if (current?._tag === 'Scheduled' || current?._tag === 'Exhausted') {
      return
    }

    if (current?._tag === 'Verifying') {
      clearTimeout(current.timer)
      if (current.attempt >= RETRY_DELAYS_MS.length) {
        this.exhaust(event.sourceId, current, event.errorClass)
        return
      }
      this.schedule(
        event.sourceId,
        current.attempt + 1,
        event.errorClass,
        current.host,
      )
      return
    }

    this.schedule(event.sourceId, 1, event.errorClass, undefined)
  }

  private schedule(
    sourceId: string,
    attempt: number,
    errorClass: string,
    host: string | undefined,
  ): void {
    const delay = RETRY_DELAYS_MS[attempt - 1]
    if (delay === undefined) {
      return
    }
    const timer = setTimeout(() => this.reload(sourceId, attempt), delay)
    this.states.set(sourceId, {
      _tag: 'Scheduled',
      attempt,
      errorClass,
      host,
      timer,
    })
  }

  private reload(sourceId: string, attempt: number): void {
    const current = this.states.get(sourceId)
    if (current?._tag !== 'Scheduled' || current.attempt !== attempt) {
      return
    }

    const timer = setTimeout(
      () => this.finishVerification(sourceId, attempt),
      VERIFICATION_TIMEOUT_MS,
    )
    this.states.set(sourceId, {
      _tag: 'Verifying',
      attempt,
      errorClass: current.errorClass,
      host: current.host,
      timer,
    })

    const result = this.runtime.reloadSource(sourceId)
    if (result._tag === 'SourceMissing') {
      this.clearSource(sourceId)
      return
    }

    const verifying = this.states.get(sourceId)
    if (verifying?._tag === 'Verifying' && verifying.attempt === attempt) {
      this.states.set(sourceId, { ...verifying, host: result.host })
    }
  }

  private finishVerification(sourceId: string, attempt: number): void {
    const current = this.states.get(sourceId)
    if (current?._tag !== 'Verifying' || current.attempt !== attempt) {
      return
    }

    const status = this.runtime.getSourceStatus(sourceId)
    if (status !== 'loading') {
      this.clearSource(sourceId)
      return
    }

    if (attempt >= RETRY_DELAYS_MS.length) {
      this.exhaust(sourceId, current, current.errorClass)
      return
    }
    this.schedule(sourceId, attempt + 1, current.errorClass, current.host)
  }

  private resetLoadedSources(): void {
    for (const [sourceId, state] of this.states) {
      if (
        state._tag === 'Verifying' &&
        this.runtime.getSourceStatus(sourceId) === 'loaded'
      ) {
        this.clearSource(sourceId)
      }
    }
  }

  private clearRemovedSources(): void {
    for (const sourceId of this.states.keys()) {
      if (this.runtime.getSourceStatus(sourceId) === 'missing') {
        this.clearSource(sourceId)
      }
    }
  }

  private exhaust(
    sourceId: string,
    state: Extract<RetryState, { _tag: 'Verifying' }>,
    errorClass: string,
  ): void {
    clearTimeout(state.timer)
    this.states.set(sourceId, { _tag: 'Exhausted' })
    this.runtime.reportExhausted({
      sourceId,
      host: state.host,
      attempts: state.attempt,
      errorClass,
    })
  }

  private clearSource(sourceId: string): void {
    const state = this.states.get(sourceId)
    if (state?._tag === 'Scheduled' || state?._tag === 'Verifying') {
      clearTimeout(state.timer)
    }
    this.states.delete(sourceId)
  }

  private clearAll(): void {
    for (const sourceId of this.states.keys()) {
      this.clearSource(sourceId)
    }
  }
}

class MapboxRuntime implements RemoteTilesetRecoveryRuntime {
  public constructor(
    private readonly map: MapboxMap,
    private readonly runtimeName: MapRuntimeName,
  ) {}

  public subscribe(listener: (event: RuntimeEvent) => void): () => void {
    const onError = (event: MapEventOf<'error'>): void => {
      if (!('sourceId' in event) || typeof event.sourceId !== 'string') {
        return
      }
      if (!isRemoteTilesetSourceId(event.sourceId)) {
        return
      }
      listener({
        _tag: 'SourceFailed',
        sourceId: event.sourceId,
        errorClass: errorClass(event.error),
      })
    }
    const onIdle = (): void => listener({ _tag: 'MapIdle' })
    const onStyleLoad = (): void => listener({ _tag: 'StyleReplaced' })
    const onStyleData = (): void => listener({ _tag: 'SourcesChanged' })

    this.map.on('error', onError)
    this.map.on('idle', onIdle)
    this.map.on('style.load', onStyleLoad)
    this.map.on('styledata', onStyleData)

    return () => {
      this.map.off('error', onError)
      this.map.off('idle', onIdle)
      this.map.off('style.load', onStyleLoad)
      this.map.off('styledata', onStyleData)
    }
  }

  public reloadSource(sourceId: string): ReloadResult {
    const source = this.map.getSource(sourceId)
    if (!source || source.type !== 'raster') {
      return { _tag: 'SourceMissing' }
    }

    const host = tileHost(source.tiles[0])
    source.reload()
    return { _tag: 'Reloaded', host }
  }

  public getSourceStatus(sourceId: string): SourceStatus {
    try {
      const source = this.map.getSource(sourceId)
      if (!source || source.type !== 'raster') {
        return 'missing'
      }
      return this.map.isSourceLoaded(sourceId) ? 'loaded' : 'loading'
    } catch {
      return 'missing'
    }
  }

  public reportExhausted(retry: ExhaustedRetry): void {
    Sentry.withScope(scope => {
      scope.setLevel('warning')
      scope.setTag('map_source', 'remote-tileset')
      scope.setTag('map_runtime', this.runtimeName)
      scope.setTag('tile_host', retry.host ?? 'unknown')
      scope.setTag('retry_attempts', String(retry.attempts))
      scope.setTag('error_class', retry.errorClass)
      Sentry.captureMessage('Remote WMTS retries exhausted')
    })
  }
}

/** Starts remote tileset recovery with an explicit runtime adapter. */
export function createRemoteTilesetRecovery(
  runtime: RemoteTilesetRecoveryRuntime,
): RemoteTilesetRecovery {
  return new Recovery(runtime)
}

export type MapRuntimeName = 'jhp' | 'mapbox-viewer'

/** Starts remote tileset recovery for a Mapbox map. */
export function attachMapboxRemoteTilesetRecovery(
  map: MapboxMap,
  // Passed in rather than sniffed from source-id prefixes, which forced distinct ids per viewer.
  // Required: a default would label a forgetful caller's failures as the wrong runtime in Sentry.
  runtimeName: MapRuntimeName,
): RemoteTilesetRecovery {
  return createRemoteTilesetRecovery(new MapboxRuntime(map, runtimeName))
}

function isRemoteTilesetSourceId(sourceId: string): boolean {
  return sourceId.startsWith(REMOTE_TILESET_LAYER)
}

function errorClass(error: unknown): string {
  if (error instanceof Error) {
    return error.name || 'Error'
  }
  return typeof error
}

function tileHost(tileUrl: string | undefined): string | undefined {
  if (!tileUrl) {
    return undefined
  }
  try {
    return new URL(tileUrl).hostname || undefined
  } catch {
    return undefined
  }
}
